import { useCallback, useMemo } from 'react'

import { Chat, Image } from '@tribeplatform/gql-client/types'
import { useUpdateGroupChat } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { FormImage } from '../../Form/FormImage/FormImage.js'
import { Form, FormMethods } from '../../Form/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { defaultGroupChatAvatar } from '../constants.js'

interface FormFields {
  name: string
  avatar: Image
}

interface Props {
  chat: Chat
  open: boolean
  onClose: () => void
}

export const UpdateGroupChatModal = ({ chat, open, onClose }: Props) => {
  const { $t } = useI18n()
  const { mutateAsync: updateGroupChat, isLoading: isUpdatingGroup } =
    useUpdateGroupChat({
      onSuccess: onClose,
    })
  const defaultValues: FormFields = useMemo(
    () => ({
      name: chat?.name ?? '',
      avatar: chat?.avatarId
        ? ({ url: chat.avatarUrl, id: chat.avatarId } as Image)
        : defaultGroupChatAvatar,
    }),
    [chat],
  )

  const onSubmit = useCallback(
    async ({ name, avatar }: FormFields, methods?: FormMethods<FormFields>) => {
      // Submitting only changed fields to properly show the edit message log
      // in the messages list.
      const dirtyFieldsKeys = Object.keys(methods?.formState.dirtyFields ?? {})
      const nameChanged = dirtyFieldsKeys.includes('name')
      const avatarChanged = dirtyFieldsKeys.includes('avatar')
      try {
        await updateGroupChat({
          chatId: chat._id,
          input: {
            name: nameChanged ? name : undefined,
            avatarId: avatarChanged ? (avatar?.id ?? null) : undefined,
          },
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Failed to update the group chat',
            id: 'Messaging.UpdateGroupChatModal.Error',
          }),
          status: 'error',
        })
      }
    },
    [$t, chat._id, updateGroupChat],
  )

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={$t({
          defaultMessage: 'Update Group',
          id: 'Messaging.UpdateGroupChatModal.Title',
        })}
      />
      <Modal.Content>
        <Form<FormFields>
          className="space-y-6"
          onSubmit={onSubmit}
          defaultValues={defaultValues}
        >
          {({ formState: { isDirty } }) => (
            <>
              <div className="flex justify-center">
                <FormImage
                  cropable
                  name="avatar"
                  placeholder={defaultGroupChatAvatar.url}
                  variant="avatar"
                  multiple={false}
                />
              </div>
              <Form.Input
                name="name"
                required
                label={$t({
                  defaultMessage: 'Group name',
                  id: 'Messaging.NewGroupChatModal.NameField.Label',
                })}
                placeholder={$t({
                  defaultMessage: 'Name of the group',
                  id: 'Messaging.NewGroupChatModal.NameField.Placeholder',
                })}
              />
              <Form.Actions>
                <Button
                  variant="primary"
                  type="submit"
                  loading={isUpdatingGroup}
                  disabled={!isDirty || !chat}
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </>
          )}
        </Form>
      </Modal.Content>
    </Modal>
  )
}
